/**
 * Lightweight analytics: captures UTM params on first visit and fires
 * pageviews + named events to /api/events (fire-and-forget, no PII).
 */
import { api } from "@/lib/api";

const KEY_ATTR = "vsk_attr";
const KEY_ATTR_PENDING = "vsk_attr_pending";
const KEY_SESSION = "vsk_sid";
const KEY_CONSENT = "vsk_consent";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

export function getConsent() {
  try {
    return localStorage.getItem(KEY_CONSENT);
  } catch {
    return null;
  }
}

export function setConsent(value) {
  try {
    localStorage.setItem(KEY_CONSENT, value);
    if (value === "all") {
      const pending = sessionStorage.getItem(KEY_ATTR_PENDING);
      if (pending) {
        localStorage.setItem(KEY_ATTR, pending);
        sessionStorage.removeItem(KEY_ATTR_PENDING);
      }
      pageview();
    }
  } catch { /* ignore */ }
}

function makeSessionId() {
  return "s_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

function getSessionId() {
  try {
    let sid = sessionStorage.getItem(KEY_SESSION);
    if (!sid) {
      sid = makeSessionId();
      sessionStorage.setItem(KEY_SESSION, sid);
    }
    return sid;
  } catch {
    return "s_none";
  }
}

export function captureAttribution() {
  try {
    const params = new URLSearchParams(window.location.search);
    const found = {};
    UTM_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) found[k] = v;
    });
    const ref = params.get("ref");
    if (ref) found.ref = ref;
    const cmp = params.get("cmp");
    if (cmp) {
      if (!found.utm_campaign) found.utm_campaign = cmp;
      if (!found.utm_source) found.utm_source = "campaign_link";
      try { localStorage.setItem("vsk_cmp", cmp); } catch { /* ignore */ }
    }
    if (Object.keys(found).length > 0) {
      const record = JSON.stringify({ ...found, at: Date.now() });
      if (getConsent() === "all") {
        localStorage.setItem(KEY_ATTR, record);
      } else {
        sessionStorage.setItem(KEY_ATTR_PENDING, record);
      }
    }
  } catch { /* ignore */ }
}

export function getAttribution() {
  try {
    const raw = localStorage.getItem(KEY_ATTR);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function track(type, extra) {
  try {
    if (getConsent() !== "all") return; // ePrivacy: analytics only with consent
    const attr = getAttribution();
    const payload = {
      type,
      path: typeof window !== "undefined" ? window.location.pathname : null,
      session_id: getSessionId(),
      utm_source: attr.utm_source || null,
      utm_medium: attr.utm_medium || null,
      utm_campaign: attr.utm_campaign || null,
      utm_content: attr.utm_content || null,
      utm_term: attr.utm_term || null,
      ref: attr.ref || null,
      extra: extra || null,
    };
    // fire-and-forget, ignore errors
    api.post("/events", payload).catch(() => {});
  } catch { /* ignore */ }
}

export function pageview() {
  track("pageview");
}
